import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_AUDIT_LOG_FILTERS,
  parseAuditLogFiltersFromSearchParams,
  serializeAuditLogFilters,
} from "../utils/auditLogsUrlSync";
import type { AuditLogFiltersState } from "../utils/auditLogsUrlSync";

interface UseAuditLogsUrlStateResult {
  filters: AuditLogFiltersState;
  /** Replace the entire filter state. Resets `page` to 1 by default. */
  setFilters: (
    next: AuditLogFiltersState | ((prev: AuditLogFiltersState) => AuditLogFiltersState),
    options?: { keepPage?: boolean }
  ) => void;
  /** Patch a subset of fields. Resets `page` to 1 by default. */
  patchFilters: (
    patch: Partial<AuditLogFiltersState>,
    options?: { keepPage?: boolean }
  ) => void;
  /** Reset every field except pagination defaults. */
  clearFilters: () => void;
  /** Page-only update; keeps every other filter unchanged. */
  setPage: (next: number) => void;
  /** Page-size update; resets page to 1. */
  setLimit: (next: number) => void;
}

/**
 * Single source of truth for the Audit Logs filter state. The state lives
 * in the URL (`useSearchParams`) so the page is linkable and "back"
 * works as expected.
 *
 * Mutations use `replace: true` to avoid creating a history entry per
 * keystroke / dropdown change — only intentional navigations push.
 */
export function useAuditLogsUrlState(): UseAuditLogsUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseAuditLogFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  const setFilters = useCallback<UseAuditLogsUrlStateResult["setFilters"]>(
    (next, options) => {
      setSearchParams(
        (current) => {
          const prev = parseAuditLogFiltersFromSearchParams(current);
          const resolved = typeof next === "function" ? next(prev) : next;
          const finalState = options?.keepPage
            ? resolved
            : { ...resolved, page: 1 };
          return serializeAuditLogFilters(finalState);
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const patchFilters = useCallback<UseAuditLogsUrlStateResult["patchFilters"]>(
    (patch, options) => {
      setFilters((prev) => ({ ...prev, ...patch }), options);
    },
    [setFilters]
  );

  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_AUDIT_LOG_FILTERS });
  }, [setFilters]);

  const setPage = useCallback(
    (next: number) => {
      setFilters((prev) => ({ ...prev, page: next }), { keepPage: true });
    },
    [setFilters]
  );

  const setLimit = useCallback(
    (next: number) => {
      setFilters((prev) => ({ ...prev, limit: next, page: 1 }), {
        keepPage: true,
      });
    },
    [setFilters]
  );

  return { filters, setFilters, patchFilters, clearFilters, setPage, setLimit };
}
